import React from "react";
import { Link } from "react-router-dom";
import useSiteContent from "@/lib/useSiteContent";

/**
 * PackageCta — the actionable footer of a priced package card (Install, Porting, Booster).
 *
 * WHY THESE TWO ACTIONS AND NOT A "BUY" BUTTON. These items have no public checkout:
 * `POST /api/tenant/billing/sku/checkout` and `POST /api/tenant/billing/booster/checkout`
 * (backend/billing_purchases_routes.py) both sit behind a practice-admin session and are
 * driven by admin/billing/PurchaseCatalogCard.jsx on the practitioner's Billing page. Nor
 * is there an unauthenticated enquiry endpoint: `solunary_service_leads` is written only
 * by `POST /api/tenant/solunary-services/interest` (backend/academy_upsell_routes.py), which is
 * owner-authenticated and keyed by service TIER, not by sku or booster. So the two steps a
 * visitor can actually take are the ones offered here — start a plan at `/platform/signup`
 * (the same destination the plan cards use) and write to the platform mailbox already
 * published as this site's "Contact sales" target and footer address. A "Buy" button would
 * have to invent a route; the section note next to these cards says where the purchase
 * really happens.
 */
const SALES_MAILBOX = "hey@solunary.health";

export default function PackageCta({ itemId, itemName, currency, tone = "midnight" }) {
  const { text } = useSiteContent();
  // The visitor's selected currency travels to signup exactly as the plan cards send it
  // (Signup.jsx re-validates it against OPERATING_CURRENCIES). Omitted when unset rather
  // than defaulted, so this file never spells a currency code of its own.
  const signupHref = currency
    ? `/platform/signup?currency=${encodeURIComponent(currency)}`
    : "/platform/signup";
  const subject = text("landing.pkg.askSubject", { item: itemName });

  return (
    <div className="mt-5 pt-4 border-t border-sol-midnight/10 flex flex-wrap items-center gap-x-4 gap-y-2">
      <Link
        to={signupHref}
        data-testid={`package-signup-${itemId}`}
        className={`inline-flex items-center rounded-xl px-3.5 py-2 text-xs font-semibold transition ${
          tone === "sage"
            ? "bg-sol-sage text-sol-ivory hover:bg-sol-sage/90"
            : "bg-sol-midnight text-sol-ivory hover:bg-sol-midnight/90"
        }`}
      >
        {text("landing.pkg.ctaSignup")}
      </Link>
      <a
        href={`mailto:${SALES_MAILBOX}?subject=${encodeURIComponent(subject)}`}
        data-testid={`package-enquire-${itemId}`}
        className="text-xs font-medium text-sol-midnight/60 underline decoration-sol-gold/60 underline-offset-2 hover:text-sol-midnight transition"
      >
        {text("landing.pkg.ctaAsk")}
      </a>
    </div>
  );
}
